// aggregate.js — pure roll-up helpers over the enriched rollup rows (each carries
// { cost:{amount,currency,priced,billed}, inr:number|null, value, day, ...dims }).
// Screens slice these by whatever dimension they need. Money that can't be priced
// or converted is tracked separately (never folded into 0).
import { daysBetween, fmtDayShort } from './format.js';

export function totalINR(items) {
  let inr = 0;
  let unpricedQty = 0;
  let unpricedCount = 0;
  const nativeByCcy = {};
  for (const it of items) {
    if (it.inr != null) inr += it.inr;
    if (it.cost && it.cost.priced && it.cost.currency) {
      nativeByCcy[it.cost.currency] = (nativeByCcy[it.cost.currency] || 0) + it.cost.amount;
    }
    if (it.cost && !it.cost.priced) { unpricedQty += it.value; unpricedCount += 1; }
  }
  return { inr, unpricedQty, unpricedCount, nativeByCcy };
}

// Group enriched rows by a key function; each group carries INR spend, native
// per-currency subtotals, row count, and unpriced tallies.
export function groupSum(items, keyFn) {
  const m = new Map();
  for (const it of items) {
    const k = keyFn(it);
    if (k === null || k === undefined) continue;
    let g = m.get(k);
    if (!g) { g = { key: k, inr: 0, count: 0, unpricedQty: 0, unpricedCount: 0, native: {} }; m.set(k, g); }
    g.count += 1;
    if (it.inr != null) g.inr += it.inr;
    if (it.cost && it.cost.priced && it.cost.currency) {
      g.native[it.cost.currency] = (g.native[it.cost.currency] || 0) + it.cost.amount;
    }
    if (it.cost && !it.cost.priced) { g.unpricedQty += it.value; g.unpricedCount += 1; }
  }
  return m;
}

// Ranked array (desc by INR) from a groupSum map, with share % of the grand total.
export function ranked(map) {
  const rows = [...map.values()];
  const grand = rows.reduce((s, r) => s + r.inr, 0);
  rows.sort((a, b) => b.inr - a.inr);
  return rows.map((r) => ({ ...r, share: grand > 0 ? (r.inr / grand) * 100 : 0 }));
}

// Per-day INR total for a subset (for sparklines / area charts).
export function dailyTotals(items, { from, to }) {
  const days = daysBetween(from, to);
  const byDay = new Map(days.map((d) => [d, 0]));
  for (const it of items) if (it.inr != null && byDay.has(it.day)) byDay.set(it.day, byDay.get(it.day) + it.inr);
  return days.map((d) => ({ day: d, label: fmtDayShort(d), value: byDay.get(d) || 0 }));
}

// Daily stacked rows: one row per day, one numeric field per series key (INR).
// keys is the ordered list of series to include; keyFn maps a row → its series key.
export function dailyStacked(items, keyFn, keys, { from, to }) {
  const days = daysBetween(from, to);
  const idx = new Map(days.map((d) => [d, { day: d, label: fmtDayShort(d) }]));
  for (const d of days) for (const k of keys) idx.get(d)[k] = 0;
  for (const it of items) {
    if (it.inr == null) continue;
    const row = idx.get(it.day);
    if (!row) continue;
    const k = keyFn(it);
    if (keys.includes(k)) row[k] += it.inr;
  }
  return days.map((d) => idx.get(d));
}

// Simple per-day quantity series for a metric subset.
export function dailyQuantity(items, { from, to }) {
  const days = daysBetween(from, to);
  const byDay = new Map(days.map((d) => [d, 0]));
  for (const it of items) if (byDay.has(it.day)) byDay.set(it.day, byDay.get(it.day) + it.value);
  return days.map((d) => ({ day: d, label: fmtDayShort(d), value: byDay.get(d) || 0 }));
}
